import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { SvgHandler } from '../../../../svg-handler/svg-handler';
import downloadIcon from '../../../../../../assets/icons/download-icon.svg';
import exclaimIcon from '../../../../../../assets/icons/exclaim.svg';
import closeIcon from '../../../../../../assets/icons/social/close.svg';
import { textWithFallback } from '../../helpers';
import './styles.m.css';
import LoaderOverlay from '../../../../molecules/loader-overlay';
import { ToastMessage } from '../../atoms/toast-message';

const DownloadIcon = () => {
  return <SvgHandler xlinkHref={downloadIcon} width='16' height='16' viewBox='0 0 16 16' clazzName='file-download' />;
};
const ExclaimIcon = () => {
  return <SvgHandler xlinkHref={exclaimIcon} width='16' height='16' viewBox='0 0 16 16' clazzName='exclaim-icon' />;
};

function formatDate ({ isoDate, locale = 'en-IN', separator = ' ' }) {
  const date = new Date(isoDate);
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }).replace(/, /g, separator);
}

async function downloadInvoice ({ invoice, subscriptionId, setPopUpData }) {
  try {
    setPopUpData({ displayPopUp: false, displayLoader: true });
    if (!global.AccessType) throw new Error('Accesstype is not loaded!');
    const invoiceResponse = await global.AccessType.downloadInvoice(subscriptionId, invoice.id);
    const bodyBlob = get(invoiceResponse, ['_bodyBlob']);
    if (!bodyBlob) throw new Error('Error fetching invoice');

    const blob = new Blob([bodyBlob], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = blobUrl;
    downloadLink.download = `Invoice_${formatDate({ isoDate: invoice['created_at'], separator: '-' })}_${
      invoice['id']
    }.pdf`;
    document.body.appendChild(downloadLink);
    downloadLink.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      })
    );
    document.body.removeChild(downloadLink);
    setPopUpData({
      displayLoader: false,
      displayPopUp: false
    });
  } catch (e) {
    console.log(e);
    setPopUpData({
      displayLoader: false,
      displayPopUp: true
    });
  }
}

export const Invoices = ({ subscriptionId, invoices, target, invoicesConfig }) => {
  const [popUpData, setPopUpData] = useState({});
  const [showInvoicesModal, setInvoicesModal] = useState(false);
  const enableDarkMode = useSelector(state => get(state, ['header', 'isDarkModeEnabled'], false));
  const mode = enableDarkMode ? 'dark' : 'light';
  const {
    dl_invoice_txt: downloadTxt,
    dl_invoice_unavailable: invoiceUnavailableTxt,
    dl_invoice_err_txt: errorMsg
  } = invoicesConfig;

  const openModal = () => {
    setInvoicesModal(true);
  };

  const closeModal = () => {
    setInvoicesModal(false);
  };

  const closeToastMsg = () =>
    setPopUpData({
      displayLoader: false,
      displayPopUp: false
    });

  if (!invoices || !Array.isArray(invoices) || !invoices.length) return null;
  if (invoices.length === 1) {
    const invoice = invoices[0];
    const downloadable = invoice.downloadable || false;
    return (
      <>
        <div
          styleName={`download-invoice ${downloadable ? 'downloadable' : 'not-downloadable'} ${mode}`}
          onClick={() => invoiceClickHandler({ invoice, subscriptionId, target, setPopUpData })}
        >
          {downloadable ? (
            <>
              <DownloadIcon />{' '}
              <span
                className='file-download'
                data-test-id='download-invoice'
                dangerouslySetInnerHTML={{ __html: textWithFallback(downloadTxt, 'Invoice') }}
              />
            </>
          ) : (
            <>
              <ExclaimIcon />{' '}
              <span
                styleName='invoice-unavailable-txt'
                data-test-id='download-invoice-unavailable'
                dangerouslySetInnerHTML={{ __html: textWithFallback(invoiceUnavailableTxt, 'Invoice not available') }}
              />
            </>
          )}
          {popUpData?.displayLoader && !popUpData?.displayPopUp && <LoaderOverlay />}
        </div>
        {!popUpData?.displayLoader && popUpData?.displayPopUp && (
          <ToastMessage
            message={textWithFallback(errorMsg, 'A technical error was encountered, please try again later.')}
            onClose={() => closeToastMsg()}
          />
        )}
      </>
    );
  }
  return (
    <>
      <div styleName={`download-invoice downloadable ${mode}`} onClick={openModal}>
        <DownloadIcon />
        <span
          className='file-download'
          data-test-id='download-invoice'
          dangerouslySetInnerHTML={{ __html: textWithFallback(downloadTxt, 'Invoice') }}
        />
      </div>
      {showInvoicesModal && (
        <InvoicesModal
          invoices={invoices}
          subscriptionId={subscriptionId}
          closeModal={closeModal}
          mode={mode}
          target={target}
          setPopUpData={setPopUpData}
          invoicesConfig={invoicesConfig}
        />
      )}
      {popUpData?.displayLoader && !popUpData?.displayPopUp && <LoaderOverlay />}
      {!popUpData?.displayLoader && popUpData?.displayPopUp && (
        <ToastMessage
          message={textWithFallback(errorMsg, 'A technical error was encountered, please try again later.')}
          onClose={() => closeToastMsg()}
        />
      )}
    </>
  );
};

const invoiceClickHandler = async ({ invoice, subscriptionId, target, setPopUpData }) => {
  if (invoice.downloadable && target === 'web') await downloadInvoice({ invoice, subscriptionId, setPopUpData });
};

const InvoicesModal = ({ invoices, subscriptionId, closeModal, mode, target, invoicesConfig, setPopUpData }) => {
  useEffect(() => {
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = 'unset';
    };
  }, []);

  const { dl_invoice_choose_invoice: chooseInvoiceTxt } = invoicesConfig;

  return (
    <>
      <div styleName='invoices-modal-background' onClick={closeModal} />
      <div data-test-id='download-invoice-modal' styleName={`invoices-modal ${mode}`}>
        <div styleName='invoices-modal-header'>
          <div
            dangerouslySetInnerHTML={{ __html: textWithFallback(chooseInvoiceTxt, 'Choose an invoice to download') }}
          />
          <div onClick={closeModal}>
            <SvgHandler
              xlinkHref={closeIcon}
              width='24'
              height='24'
              viewBox='0 0 24 24'
              clazzName='close-invoices-modal'
            />
          </div>
        </div>
        <div styleName='invoices-list'>
          {invoices.map((invoice, index) => {
            return (
              <div key={invoice.id}>
                <div
                  onClick={() => invoiceClickHandler({ invoice, subscriptionId, target, setPopUpData })}
                  styleName={`invoice-link ${invoice.downloadable ? 'downloadable' : 'not-downloadable'} ${mode}`}
                >
                  {formatDate({ isoDate: invoice['created_at'] })}
                </div>
                {index !== invoices.length - 1 && <hr styleName='invoice-separator' />}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
